<!--
  CampaignEditShell.vue — Claude Design redesign experiment shell.

  Replaces the v-tabs + v-window chrome of CampaignAddEdit with:
   - Left sidebar: 12 sections grouped Setup / Catalog / Operations / Other
     (+ Review & Launch as the entry-point landing).
   - Main content area: per-section page header + slot for the section
     component. Section renders via a named slot the parent fills, so this
     shell stays generic and the parent (CampaignAddEdit) keeps owning
     event handlers, refs, and validation.
   - Sticky pager (top-right): Previous / counter / Next.
   - Storefront preview drawer (right slide-in).
   - Sidebar footer: Preview Storefront, Revert button.

  Nested-route navigation: /distributor/stores/:id/edit/:section? is the
  canonical URL. activeSectionId is a computed off $route.params.section
  (empty -> Review and Launch landing). goTo(id) calls $router.push;
  there is no window.location.hash usage. Bookmarked legacy hashes
  (#payment, #managers, etc.) are routed through redirectLegacyHash()
  in mounted(), which router.replace's to the equivalent /edit/:section.
  Hash is reserved for sub-tabs within a section (e.g.
  pricing-checkout#coupons-panel) and never drives section nav. The
  parent listens for active-section-changed and triggers any
  side-effects (validation runs, decoration list reloads, etc.).

  Autosave + revert lifecycle:
   - On mount, parent calls /campaigns/:id/start_edit_session via this
     shell's setupSession(). Stashes the session id locally.
   - "Revert" button calls /revert_edit_session and emits 'revert-completed'
     so parent reloads the campaign data.
   - On unmount (or saveAndExit), parent calls commit_edit_session.
-->
<template>
  <div class="ces-root">
    <aside
      class="ces-sidebar"
      :class="{ 'ces-sidebar--collapsed': sidebarCollapsed }"
      :style="mainHeightPx ? { '--ces-main-h': mainHeightPx + 'px' } : null"
    >
      <div class="ces-sidebar__head">
        <div class="ces-sidebar__lbl">{{ archivedMode ? "Archived store" : "Editing store" }}</div>
        <div class="ces-sidebar__name" :title="campaignNameDisplay">{{ campaignNameDisplay }}</div>
        <div class="ces-sidebar__meta">
          <span class="ces-pill" :class="statusPillClass" data-testid="ces-status-pill">{{ statusPillLabel }}</span>
          <span v-if="dateMeta" class="ces-sidebar__dates" data-testid="ces-sidebar-dates">{{ dateMeta }}</span>
        </div>
      </div>

      <a
        v-if="!archivedMode"
        class="ces-step ces-step--review"
        :class="{ 'ces-step--act': activeSectionId === 'review' }"
        :href="sectionHref('review')"
        @click.prevent="goTo('review')"
        data-cy="ces-nav-review"
        data-testid="ct-review"
      >
        <div class="ces-step__icon"><span class="tf-icon">checklist</span></div>
        <div class="ces-step__meta">
          <div class="ces-step__t">Review &amp; Launch</div>
          <div class="ces-step__s">{{ reviewSubtitle }}</div>
        </div>
      </a>

      <template v-for="grp in sectionGroups" :key="grp.label">
        <div class="ces-grp">{{ grp.label }}</div>
        <a
          v-for="sec in grp.sections"
          :key="sec.id"
          class="ces-step"
          :class="{
            'ces-step--act': activeSectionId === sec.id,
            'ces-step--done': sectionStatus[sec.id] === 'done',
            'ces-step--warn': sectionStatus[sec.id] === 'warn',
            'ces-step--staff-only': sec.staffOnly,
          }"
          :href="sectionHref(sec.id)"
          @click.prevent="goTo(sec.id)"
          :data-cy="`ces-nav-${sec.id}`"
          :data-testid="`ct-${sec.id}`"
          :data-tip="sec.title"
        >
          <div class="ces-step__icon"><span class="tf-icon">{{ sec.icon }}</span></div>
          <div class="ces-step__meta">
            <div class="ces-step__t">{{ sec.title }}</div>
            <div class="ces-step__s">{{ sectionSubtitle[sec.id] || sec.subtitle || '' }}</div>
          </div>
          <span class="ces-step__check tf-icon" v-if="sectionStatus[sec.id] === 'done'">check</span>
          <span class="ces-step__check ces-step__check--err tf-icon" v-else-if="sectionStatus[sec.id] === 'warn'">error</span>
        </a>
      </template>

      <!-- An archived store has no storefront to preview and no edit session
           to revert: both are gone with the rest of the editor. -->
      <div class="ces-sidebar__foot" v-if="!archivedMode">
        <button class="ces-ghost-btn" @click="openPreview" data-cy="ces-preview-btn">
          <span class="tf-icon">visibility</span>
          <span>Preview Storefront</span>
        </button>
        <button
          class="ces-ghost-btn"
          @click="confirmRevert"
          :disabled="!editSessionId"
          data-cy="ces-revert-btn"
          :title="editSessionId ? 'Discard all changes since you opened this store' : 'No edit session active'"
        >
          <span class="tf-icon">restore</span>
          <span>Revert Unsaved Changes</span>
        </button>
      </div>
    </aside>

    <main class="ces-content">
      <div class="ces-pager" data-testid="ces-pager" v-if="activeSectionId !== 'review' && !archivedMode">
        <div class="ces-pager__pill">
          <a
            class="ces-pager__btn"
            :class="{ 'ces-pager__btn--disabled': !prevSection }"
            :href="prevSection ? sectionHref(prevSection.id) : ''"
            @click.prevent="prevSection && goTo(prevSection.id)"
          >
            <span class="tf-icon">chevron_left</span>
            <span>Previous</span>
          </a>
          <span class="ces-pager__counter">{{ pagerCounter }}</span>
          <a
            class="ces-pager__btn ces-pager__btn--next"
            :class="{ 'ces-pager__btn--disabled': !nextSection }"
            :href="nextSection ? sectionHref(nextSection.id) : ''"
            @click.prevent="nextSection && goTo(nextSection.id)"
          >
            <span>Next</span>
            <span class="tf-icon">chevron_right</span>
          </a>
        </div>
      </div>

      <editor-section-header
        v-if="activeSection"
        class="ces-page-head"
        :eyebrow="activeSectionEyebrow"
        :title="activeSection.title"
        :description="activeSection.description"
      />

      <!--
        Section content renders via named slot per section id.
        Parent (CampaignAddEdit) supplies the actual <campaign-*> components.
        This keeps refs, event handlers, and validation owned by parent.
      -->
      <!--
        `v-if`, not `v-show`, on the SLOT REQUEST. When activeSectionId is
        'review' this generic branch would otherwise ask for the slot named
        `section-review` -- the very slot the dedicated branch below renders
        -- so the review pane mounted TWICE: once here (hidden by v-show,
        and handed only `section-id`, so its `openPreview` slot prop is
        undefined) and once below (visible, correctly scoped).

        Both copies carry the same data-testids. A testid-driven click takes
        the FIRST match in document order, which is the hidden, mis-scoped
        copy -- so clicking `review-launch-preview-btn` reported success and
        did nothing, because the handler it reached was not bound to
        openPreview. That is ui-runner scenario dm-store-preview-interactive
        timing out on `ces-preview-iframe` after a "passing" click.

        The outer v-show is kept so a real section keeps its DOM (and any
        scroll/input state) across section switches; only the review case
        is excluded, and `review` is deliberately NOT in SECTION_IDS
        (sectionRoutes.js) -- it is the fallback landing, never a section
        this branch should serve.
      -->
      <!-- Editor-wide notices (e.g. a refused save), above every section. -->
      <slot name="banner"></slot>

      <div class="ces-section" v-show="activeSectionId !== 'review'">
        <slot
          v-if="activeSectionId !== 'review'"
          :name="`section-${activeSectionId}`"
          :section-id="activeSectionId"
        ></slot>
      </div>

      <!-- Not mounted at all for an archived store: the Review pane fetches
           readiness and the like, none of which an archived store serves. -->
      <div class="ces-section" v-if="!archivedMode" v-show="activeSectionId === 'review'">
        <slot
          name="section-review"
          :status="sectionStatus"
          :sections="allSections"
          :open-preview="openPreview"
        ></slot>
      </div>
    </main>

    <!-- Storefront preview drawer -->
    <div class="ces-preview-scrim" :class="{ 'open': previewOpen }" @click="closePreview"></div>
    <aside class="ces-preview-drawer" :class="{ 'open': previewOpen }">
      <div class="ces-preview-head">
        <div>
          <div class="ces-preview-ttl">
            Storefront preview
            <!--
              Not-launched badge (Q2). Shown only when the previewed
              store is inactive (stores.active = false). The owner is
              previewing a draft that shoppers cannot yet reach on the
              public URL; the pill makes that unambiguous. Lives in the
              preview CHROME (not inside the iframe content) so it is
              always visible and never affects what the shopper would
              actually see post-launch. Driven by the parent's
              campaign.active -- no cross-frame plumbing needed.
            -->
            <span
              v-if="isPreviewNotLaunched"
              class="ces-preview-badge"
              data-testid="preview-not-launched-badge"
            >PREVIEW - not yet launched</span>
          </div>
          <div class="ces-preview-meta">{{ campaignNameDisplay }} — what employees will see</div>
        </div>
        <div class="ces-preview-seg">
          <button :class="{ act: previewVp === 'desktop' }" @click="previewVp = 'desktop'">
            <span class="tf-icon">desktop_windows</span>Desktop
          </button>
          <button :class="{ act: previewVp === 'mobile' }" @click="previewVp = 'mobile'">
            <span class="tf-icon">smartphone</span>Mobile
          </button>
        </div>
        <div class="ces-preview-close" @click="closePreview" title="Close preview">
          <span class="tf-icon">close</span>
        </div>
      </div>
      <!--
        Live-store link (Trello #474). Only when the store is LIVE (same
        deriveStoreStatus() as the status pill) and has a public URL
        (previewUrl, the same value as the caption link below). Draft,
        closed, archived and unsaved stores render nothing.
      -->
      <div v-if="isStoreLive && previewUrl" class="ces-preview-live">
        <a
          :href="previewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="ces-preview-live__link"
          data-testid="ces-preview-live-link"
        ><span class="tf-icon">open_in_new</span>Open live store</a>
      </div>
      <!--
        Storefront preview. When the store has a `slug`, embed the live
        public storefront (`/store/<slug>?preview=1`) in an INTERACTIVE
        iframe: the distributor can scroll, click, and browse the store
        the way a shopper would (2026-08-18, Jake -- the transparent
        click-swallowing overlay that used to sit here was removed).

        What is NOT removed is the SESSION guard, which protects a
        different thing: `?preview=1` (previewIframeUrl) tells the
        storefront to skip its shopper token swap. The iframe is
        same-origin and shares this editor's localStorage, so an
        unguarded swap would clobber the editor's (impersonation) auth
        token. Because the iframe is now live, that flag must survive
        in-iframe NAVIGATION too -- in-storefront links are bare path
        strings that would otherwise drop the query on the first click.
        `installPreviewQueryPersistence` (router/storefrontPreview.js,
        wired in router/index.js) keeps it attached; the
        privileged-session check (router/privilegedSession.js) is the
        independent second line of defence.

        The open-in-new-tab link is `previewPopoutUrl` (Jake, 2026-10-22):
        for a LIVE store the plain public URL, so the owner sees what
        shoppers see and can place a test order; for a store that is NOT
        live, the same owner preview as this iframe (?preview=1), since a
        shopper visit to it is "Store not found". A new tab is NOT isolated
        from this editor: same-origin tabs share localStorage, so the flag
        also keeps the storefront from swapping this editor's token there
        (router/privilegedSession.js guards the live link's plain visit).
        When there is no slug yet (never saved), the empty-state renders
        instead.
      -->
      <div class="ces-preview-body" v-if="previewOpen">
        <template v-if="previewUrl">
          <div class="ces-preview-stage" :class="`ces-preview-stage--${previewVp}`">
            <div class="ces-preview-frame">
              <iframe
                :src="previewIframeUrl"
                :class="previewVp"
                :title="`Live storefront preview for ${campaignNameDisplay}`"
                loading="lazy"
                data-testid="ces-preview-iframe"
              ></iframe>
            </div>
            <p v-if="isStoreLive" class="ces-preview-caption">
              Browse the preview as a shopper would. Cart and checkout are
              disabled here --
              <a
                :href="previewPopoutUrl"
                target="_blank"
                rel="noopener"
                class="ces-preview-link"
                data-testid="ces-open-storefront"
              >open in a new tab</a>
              to place a test order.
            </p>
            <p v-else class="ces-preview-caption">
              Browse the preview as a shopper would. Cart and checkout are
              disabled until the store is live --
              <a
                :href="previewPopoutUrl"
                target="_blank"
                rel="noopener"
                class="ces-preview-link"
                data-testid="ces-open-storefront"
              >open the preview in a new tab</a>.
            </p>
          </div>
        </template>
        <div v-else class="ces-preview-empty" data-testid="ces-preview-empty">
          <span class="tf-icon">storefront</span>
          <p style="margin-bottom: 8px;"><strong>No storefront URL yet</strong></p>
          <p style="max-width: 480px;">
            Save the store to generate its public link, then the
            storefront preview will show the live shopper view here.
          </p>
        </div>
      </div>
    </aside>

    <!-- Revert confirmation -->
    <v-dialog v-model="revertDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Discard all changes?</v-card-title>
        <v-card-text>
          This will restore <strong>{{ campaignNameDisplay }}</strong> to the state it was in
          when you opened the editor. Any edits you've made will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="revertDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="doRevert" :loading="reverting">Discard Changes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
/* eslint-disable vue/no-mutating-props */
import { SECTION_IDS, OLD_HASH_TO_SECTION } from "./sectionRoutes.js";
import { deriveStoreStatus } from "./storeStatus.js";
import {
  campaignHasOwnZone,
  campaignScheduleZone,
  fetchPlatformTimezone,
  windowMomentLabel,
} from "./storeWindow.js";
import EditorSectionHeader from "./components/EditorSectionHeader.vue";

// The editor pill speaks the Stores list's words (StatusChip), from the same
// helper, so the two never disagree about a store. It used to call a
// complete store "Launched" while the list called it Closed (spec
// docs/planning/store-counting-rules-2026-10-15.md 1.1).
const STATUS_PILL = {
  live: { label: "Live", cls: "ces-pill--launched" },
  draft: { label: "Draft", cls: "ces-pill--draft" },
  closed: { label: "Closed", cls: "ces-pill--closed" },
  archived: { label: "Archived", cls: "ces-pill--draft" },
};

// Section eyebrow per the 2026-05-29 campaign-editor design handoff.
// Each eyebrow names the sidebar group the active section belongs to,
// so a reader scanning between the eyebrow and the sidebar always sees
// the same words.
//
// History: A3a's first cut used the design's four-letter taxonomy
// STATUS / SETUP / CATALOG / OPERATIONS, anticipating an A4 sidebar
// regroup that merged "Money & Checkout" + "People & Comms" into a
// single "Operations" group. Jake decided 2026-06-17 to keep the
// existing four-group sidebar split (Money & Checkout stays separate
// from People & Comms). To preserve the eyebrow-matches-sidebar rule,
// the OPERATIONS slot was replaced with the two sidebar group labels:
// MONEY & CHECKOUT for the money-axis pages, PEOPLE & COMMS for the
// human-axis pages.
const SECTION_EYEBROW = Object.freeze({
  review:             "STATUS",
  "client-store":     "SETUP",
  branding:           "SETUP",
  "store-login":      "SETUP",
  defaults:           "SETUP",
  products:           "CATALOG",
  categories:         "CATALOG",
  decorations:        "CATALOG",
  // PR-G (2026-06-22): Checkout IA refactor split the legacy
  // "Pricing & Checkout" section into separate Pricing and Checkout
  // siblings, both in the Money & Checkout sidebar group.
  pricing:            "MONEY & CHECKOUT",
  checkout:           "MONEY & CHECKOUT",
  "tax-legal":        "MONEY & CHECKOUT",
  team:               "PEOPLE & COMMS",
  notifications:      "PEOPLE & COMMS",
});

// The ONLY section an ARCHIVED store's editor offers (Jake, 2026-10-20: the
// restricted Batches-only mode). The server's entry check
// (`/campaign/getUserHasAccessToCampaign?section=batches`) admits the same
// one section, so the two cannot drift apart.
const ARCHIVED_STORE_SECTION = 'batches';

/*
 * Flush-layout escape from DistributorLayout's `.dist-main { padding: 24px }`.
 *
 * Jake, 2026-08-26: the editor left-nav had top and left margins pushing it
 * away from the page header and the distributor left-nav. Those margins were
 * never the shell's own -- they are the app shell's shared 24px gutter, which
 * every OTHER distributor page still wants. So the shell opts itself out for
 * its own lifetime with a body class rather than changing the layout rule.
 * Same mechanism ProductEditor.vue already uses (`tf-product-editor-active`).
 *
 * REFCOUNTED because vue-router can hold two shell instances alive at once
 * during a section transition (the incoming one mounts before the outgoing
 * one unmounts). A plain add-on-mount / remove-on-unmount pair would let the
 * outgoing instance strip the class out from under the incoming one and
 * flash the gap back in mid-navigation.
 */
const FLUSH_BODY_CLASS = "tf-store-editor-active";
let flushRefCount = 0;

function acquireFlushLayout() {
  flushRefCount += 1;
  document.body.classList.add(FLUSH_BODY_CLASS);
}

function releaseFlushLayout() {
  flushRefCount = Math.max(0, flushRefCount - 1);
  if (flushRefCount === 0) {
    document.body.classList.remove(FLUSH_BODY_CLASS);
  }
}

export default {
  name: "StoreEditShell",
  components: {
    EditorSectionHeader,
  },
  props: {
    campaign:        { type: Object, required: true },
    editMode:        { type: Boolean, default: false },
    sectionStatus:   { type: Object, default: () => ({}) },     // { sectionId: 'done'|'warn'|null }
    sectionSubtitle: { type: Object, default: () => ({}) },     // { sectionId: 'subtitle line' }
  },
  emits: [
    'active-section-changed',
    'edit-session-started',
    'revert-completed',
    // Emitted when the user navigates while the store has no id yet
    // (the /distributor/stores/add flow). The shell cannot build a
    // real nested URL without an id, so it delegates to the parent
    // (StoreAddEdit) which owns the create -> capture-id -> route path.
    // Fixes #1827 (new store never persisted; URL contained `null`).
    'navigate-section',
  ],
  data() {
    return {
      sidebarCollapsed: false,
      // Visible height of the scrolling .dist-main ancestor; bounds the sidebar.
      mainHeightPx: 0,
      previewOpen: false,
      previewVp: 'desktop',
      revertDialog: false,
      reverting: false,
      editSessionId: null,
      // undefined = not looked up yet; null = none set / lookup failed.
      platformTimezone: undefined,
    };
  },
  computed: {
    // True for an archived store: the editor shows only its Batches.
    archivedMode() {
      return !!this.campaign?.archived_at;
    },
    activeSectionId() {
      if (this.archivedMode) return ARCHIVED_STORE_SECTION;
      // The active section is derived from the nested route param. The
      // bare `/edit` URL (no section param) is the Review and Launch
      // landing, represented internally by the id 'review'. An unknown
      // param value also falls back to the landing so a stale deep-link
      // never renders an empty section panel.
      const section = this.$route?.params?.section || '';
      if (!section) return 'review';
      return SECTION_IDS.includes(section) ? section : 'review';
    },
    sectionGroups() {
      if (this.archivedMode) {
        return [
          {
            label: 'Operations',
            sections: [
              { id: ARCHIVED_STORE_SECTION, title: 'Batches', icon: 'inventory', subtitle: '', staffOnly: true },
            ],
          },
        ];
      }
      // IA per locked design (campaign-editor PLAN.md "Section
      // consolidations" + UX review #4 group split): four peer groups
      // Setup > Money & Checkout > Catalog > People & Comms.
      //
      // 2026-08-12 (Jake): Money & Checkout moved above Catalog (it
      // previously sat between Catalog and People & Comms). Group order
      // here is the ONLY source of sidebar order, and allSections (the
      // prev/next pager) derives from it, so the pager follows the nav.
      //
      // Section ids are the canonical nested-route section ids (see
      // sectionRoutes.js SECTION_IDS) — the same values that appear in the
      // URL `/distributor/stores/:id/edit/:section`. The Round-2
      // consolidations are applied here: name + client fold into one
      // Client & Store section, managers + departments fold into one Team
      // section, and Fulfillment is dropped entirely.
      return [
        {
          label: 'Setup',
          sections: [
            // 2026-08-12 (Jake): "Client & Store" -> "Client & Store Details".
            // Title only -- the 'client-store' id is the canonical nested-route
            // section id (see sectionRoutes.js), so renaming the title leaves
            // the URL and every route reference untouched. Kept in lock-step
            // with sectionRoutes.js::SECTION_META by contract.
            { id: 'client-store',    title: 'Client & Store Details', icon: 'event',        subtitle: '' },
            { id: 'branding',        title: 'Branding & Home Page',  icon: 'palette',       subtitle: '' },
            { id: 'store-login',     title: 'Store Login',           icon: 'vpn_key',       subtitle: '' },
            // 'defaults' hidden 2026-08-12 (Jake): the section was judged not
            // to earn its place in the nav. The UI is hidden ONLY -- the
            // backend is untouched: /api/stores/<id>/defaults/ still serves,
            // and apply_store_defaults_to_new_product (campaign_product.py:588)
            // still stamps any already-stored decoration set / colors / margin
            // onto new products, so the Distributor -> Store -> Product margin
            // chain is unchanged. StoreDefaultsSection.vue is still registered
            // in StoreAddEdit.vue. To restore, re-add the line below:
            // { id: 'defaults',     title: 'Defaults',              icon: 'auto_awesome',  subtitle: '' },
          ],
        },
        {
          // Money-axis sections — how Owners think about cost/payment.
          // PR-G (2026-06-22): the legacy "Pricing & Checkout" was split
          // into Pricing (left intact) and Checkout (lifted from Setup
          // and broadened to host Shopper Allowance + Store Limits). The
          // two sit next to Tax & Legal under the same Money & Checkout
          // heading so the eyebrow + sidebar match.
          //
          // 2026-08-12 (Jake): this group moved UP from below Catalog to
          // sit directly under Setup -- the money-axis setup is
          // configured before the catalog is stocked. Group INTERNAL
          // order (Pricing, Checkout, Tax & Legal) is unchanged.
          label: 'Money & Checkout',
          sections: [
            { id: 'pricing',   title: 'Pricing',     icon: 'credit_card', subtitle: '', staffOnly: true },
            { id: 'checkout',  title: 'Checkout',    icon: 'list_alt',    subtitle: '', staffOnly: true },
            // 2026-08-20 (ADR 0066): orders collect into batches and are
            // charged at release, so Batches sits on the money axis beside
            // Checkout rather than in Catalog or People.
            { id: 'batches',   title: 'Batches',     icon: 'inventory',   subtitle: '', staffOnly: true },
            { id: 'tax-legal', title: 'Tax & Legal', icon: 'gavel',       subtitle: '', staffOnly: true },
          ],
        },
        {
          // Catalog order is the ADR 0059 locked design exactly --
          // Products, Categories & Storefront, Decorations
          // (design/Locked Decisions.md "Chrome"). C1 (2026-07-24) added
          // Categories & Storefront between Products and Decorations and
          // flipped the prior Decorations-then-Products order to match
          // the design, per Jake's confirmation.
          label: 'Catalog',
          sections: [
            { id: 'products',     title: 'Products',       icon: 'inventory_2',      subtitle: '' },
            // C1 (2026-07-24, ADR 0059): Categories & Storefront -- group
            // products for shoppers, shape the storefront nav, and set the
            // home page. Sub-text is the live category count, wired via
            // sectionSubtitle (falls back to this static default before
            // the count loads).
            { id: 'categories',   title: 'Categories & Storefront', icon: 'storefront', subtitle: '' },
            { id: 'decorations',  title: 'Decorations',    icon: 'design_services',  subtitle: '', description: "Every decoration this store uses, listed once. Clear approvals, check each mark on its garment, and extend a decoration across more products -- all in place. Review & Launch still owns the launch gate." },
          ],
        },
        {
          // Human-axis sections — people and communications.
          label: 'People & Comms',
          sections: [
            { id: 'team',          title: 'Team & Departments', icon: 'supervisor_account', subtitle: '' },
            { id: 'notifications', title: 'Email Notifications', icon: 'mail',               subtitle: '' },
          ],
        },
      ];
    },
    allSections() {
      // Flat list, ordered by sidebar appearance — used for prev/next pager.
      const flat = [{ id: 'review', title: 'Review & Launch' }];
      this.sectionGroups.forEach(g => g.sections.forEach(s => flat.push(s)));
      return flat;
    },
    activeSection() {
      return this.allSections.find(s => s.id === this.activeSectionId);
    },
    activeSectionEyebrow() {
      // Page-head eyebrow per the 2026-05-29 design handoff. Resolves
      // through the SECTION_EYEBROW map; unknown sections fall back to the
      // empty string (renders no eyebrow) rather than throwing.
      return SECTION_EYEBROW[this.activeSectionId] || "";
    },
    pagerCounter() {
      const idx = this.allSections.findIndex(s => s.id === this.activeSectionId);
      return idx >= 0 ? `${idx + 1} / ${this.allSections.length}` : '';
    },
    prevSection() {
      const idx = this.allSections.findIndex(s => s.id === this.activeSectionId);
      return idx > 0 ? this.allSections[idx - 1] : null;
    },
    nextSection() {
      const idx = this.allSections.findIndex(s => s.id === this.activeSectionId);
      return idx >= 0 && idx < this.allSections.length - 1
        ? this.allSections[idx + 1]
        : null;
    },
    campaignNameDisplay() {
      return this.campaign?.name || 'New Store';
    },
    statusPill() {
      return STATUS_PILL[deriveStoreStatus(this.campaign)] || STATUS_PILL.draft;
    },
    statusPillClass() {
      return this.statusPill.cls;
    },
    statusPillLabel() {
      return this.statusPill.label;
    },
    dateMeta() {
      // A Permanent store has no scheduled launch (Bug Backlog #329).
      if (this.campaign?.is_permanent_store) return '';
      const start = this.campaign?.start_date;
      if (!start) return '';
      // The launch moment is the open date AND time, in the zone the
      // schedule is read in (card #497). Held back until that zone is known
      // so the header never says "UTC" for a store that is not.
      if (!campaignHasOwnZone(this.campaign) && this.platformTimezone === undefined) return '';
      const label = windowMomentLabel(
        start,
        campaignScheduleZone(this.campaign, this.platformTimezone),
        { withDate: true }
      );
      return label ? `Launches ${label}` : '';
    },
    reviewSubtitle() {
      const total = this.allSections.length - 1; // exclude review itself
      const done = Object.values(this.sectionStatus).filter(s => s === 'done').length;
      return `${done} of ${total} sections complete`;
    },
    isStoreLive() {
      // Conservative: only the "live" status (active, not archived, not
      // closed/past its end date). Same helper as statusPill.
      return deriveStoreStatus(this.campaign) === "live";
    },
    isPreviewNotLaunched() {
      // True when the store being previewed has not launched
      // (stores.active = false). Drives the "PREVIEW - not yet launched"
      // badge in the preview chrome. A store is launched when active is
      // truthy; a brand-new store (no id yet) or an explicitly inactive
      // store is not launched. Only meaningful while a slug exists (the
      // iframe renders), but the computed is cheap and the badge only
      // shows inside the preview drawer.
      if (!this.campaign?.id) return false;
      return !this.campaign?.active;
    },
    previewUrl() {
      // Build the canonical public storefront URL from the store's
      // `slug` column -- NOT the numeric id and NOT `shopper_url`.
      //
      // `slug` is the unique, always-present storefront routing key: the
      // ORM derives it on create (from shopper_url or name), it is the
      // segment `/store/:slug/products` resolves against, and every
      // shopper-side URL builder uses it. `shopper_url` is the legacy
      // "Custom Link" that is frequently NULL or the sentinel string
      // "none" (e.g. store 13: shopper_url="none", slug="new-store"),
      // which previously produced a broken/ambiguous preview URL.
      //
      // Absolute (origin + /store/<slug>) so it works as the open-in-new-
      // tab link. The iframe uses `previewIframeUrl` (this + ?preview=1).
      // Null only when the store has no slug yet (never saved) -- the
      // empty-state renders instead of the iframe.
      if (!this.campaign?.id || !this.campaign?.slug) return null;
      return `${window.location.origin}/store/${this.campaign.slug}`;
    },
    previewIframeUrl() {
      // The preview iframe is SAME-ORIGIN and shares localStorage with
      // this editor. The storefront's normal shopper "token swap" would
      // overwrite the editor's auth token (e.g. a superadmin impersonating
      // a distributor), downgrading every subsequent editor API call to a
      // Shopper identity (500/403). `?preview=1` tells the storefront it
      // is a read-only preview and MUST skip the token swap / shopper
      // login -- see router/storefrontPreview.js. It is also what lets the
      // API answer the owner's preview of a store that is not live
      // (router/storefrontPreviewRequest.js).
      if (!this.previewUrl) return null;
      return `${this.previewUrl}?preview=1`;
    },
    previewPopoutUrl() {
      // The "open in a new tab" link (Jake, 2026-10-22). A LIVE store opens
      // the real public storefront (plain URL): the owner sees what
      // shoppers see and can place a test order. A store that is NOT live
      // (draft, closed, archived) opens the same owner preview the iframe
      // shows -- a plain shopper visit to it is "Store not found". The new
      // tab shares this origin's localStorage, so the preview flag also
      // keeps the storefront from swapping this editor's token there.
      return this.isStoreLive ? this.previewUrl : this.previewIframeUrl;
    },
  },
  mounted() {
    // The platform default zone, only when the distributor names none.
    if (campaignHasOwnZone(this.campaign)) {
      this.platformTimezone = null;
    } else {
      fetchPlatformTimezone(this.$axios).then((tz) => { this.platformTimezone = tz; });
    }
    // Sit flush against the page header and the distributor left-nav by
    // dropping the app shell's 24px gutter for this route only.
    acquireFlushLayout();
    this.observeMainHeight();
    // Legacy deep-link shim: a stale `#hash` URL (e.g. `#payment`) is
    // translated to its new nested-route section and the URL is replaced
    // in place so old bookmarks land on the right section. After this the
    // route param is the single source of truth.
    this.redirectLegacyHash();
    // Announce the initial active section so the parent (StoreAddEdit)
    // can lazy-load the corresponding section's data on first render.
    this.$emit('active-section-changed', this.activeSectionId);
    document.addEventListener('keydown', this.handleEscape);
    if (this.editMode && this.campaign?.id && !this.archivedMode) {
      this.setupSession();
    }
  },
  beforeUnmount() {
    releaseFlushLayout();
    if (this.mainResizeObserver) {
      this.mainResizeObserver.disconnect();
      this.mainResizeObserver = null;
    }
    document.removeEventListener('keydown', this.handleEscape);
    if (this.editSessionId) {
      this.commitSession();  // fire-and-forget
    }
  },
  watch: {
    'campaign.id'(newId) {
      if (newId && this.editMode && !this.editSessionId && !this.archivedMode) {
        this.setupSession();
      }
    },
    // Unarchiving from the banner flips the editor back to the full one;
    // open the edit session an archived store never had.
    archivedMode(now, before) {
      if (before && !now && this.editMode && this.campaign?.id && !this.editSessionId) {
        this.setupSession();
      }
    },
    // The active section now flows from the route param. Re-emit
    // `active-section-changed` whenever it changes so the parent's
    // lazy-load handler fires exactly once per section navigation.
    activeSectionId(newId) {
      this.$emit('active-section-changed', newId);
    },
  },
  methods: {
    /**
     * Bound the sidebar by the REAL visible height of the scrolling
     * `.dist-main` ancestor. CSS `max-height: 100%` cannot do it: the
     * sidebar's grid cell is as tall as the whole section body, so the
     * percentage never resolves to the viewport and the nav cannot scroll on
     * a short window. Measuring (instead of `100vh - <topbar>`) keeps the
     * topbar, impersonation banner and Vuetify chrome out of this file.
     */
    observeMainHeight() {
      const main = this.$el?.closest?.('.dist-main');
      if (!main) return;
      const measure = () => { this.mainHeightPx = main.clientHeight || 0; };
      measure();
      if (typeof ResizeObserver === 'function') {
        this.mainResizeObserver = new ResizeObserver(measure);
        this.mainResizeObserver.observe(main);
      }
    },
    /**
     * Build the nested-route href for a section id. The Review and Launch
     * landing ('review') is the bare `/edit` URL; every other section is
     * `/edit/<section>`. Used for the `href` attribute so the links remain
     * real, copyable URLs (the `@click.prevent` handler does the SPA push).
     */
    sectionHref(id) {
      // Without a persisted id (the /stores/add flow) there is no real
      // nested URL to point at. Return the id-less `/stores/add` path so
      // the rendered `href` never contains a `null`/`undefined` id
      // segment. The `@click.prevent` handler (goTo) delegates the actual
      // navigation to the parent for the create-first path. Fixes #1827.
      if (!this.campaign?.id) return '/distributor/stores/add';
      const base = `/distributor/stores/${this.campaign.id}/edit`;
      return id === 'review' ? base : `${base}/${id}`;
    },
    goTo(id) {
      if (this.activeSectionId === id) return;
      // New-store flow: no id yet. The shell can't build a real nested
      // URL, and pushing one with a placeholder id produced the
      // `/stores/null/edit/...` bug (#1827). Delegate to the parent, which
      // owns saveCampaign(): it creates the store, captures the id, then
      // routes to this section. The shell does NOT push here.
      if (!this.campaign?.id) {
        this.$emit('navigate-section', id);
        return;
      }
      // Navigation is a real route change: push the nested URL. The route
      // watcher recomputes `activeSectionId` and emits exactly once, so
      // the parent's section load handler is never double-fired.
      this.$router.push(this.sectionHref(id));
    },
    /**
     * Translate a stale legacy `#hash` URL to the new nested-route section
     * and replace the URL in place. No-op when there is no hash.
     */
    redirectLegacyHash() {
      const raw = (this.$route?.hash || '').replace(/^#/, '');
      if (!raw) return;
      const mapped = Object.prototype.hasOwnProperty.call(OLD_HASH_TO_SECTION, raw)
        ? OLD_HASH_TO_SECTION[raw]
        : '';
      this.$router.replace(this.sectionHref(mapped || 'review'));
    },
    handleEscape(e) {
      if (e.key === 'Escape' && this.previewOpen) this.closePreview();
    },
    openPreview() { this.previewOpen = true; },
    closePreview() { this.previewOpen = false; },
    async setupSession() {
      try {
        const resp = await this.$axios.post(`/api/stores/${this.campaign.id}/start_edit_session`, { meta: { method: 'start_edit_session', args: {} } });
        const result = resp.data?.meta?.result || {};
        this.editSessionId = result.session_id || null;
        this.$emit('edit-session-started', result);
      } catch (err) {
        // Non-fatal — Revert won't be available without a session.
        console.warn('[CampaignEditShell] start_edit_session failed', describeError(err));
      }
    },
    async commitSession() {
      if (!this.editSessionId) return;
      try {
        await this.$axios.post(`/api/stores/${this.campaign.id}/commit_edit_session`, { meta: { method: 'commit_edit_session', args: {} } });
      } catch (err) {
        console.warn('[CampaignEditShell] commit_edit_session failed', describeError(err));
      }
      this.editSessionId = null;
    },
    confirmRevert() { this.revertDialog = true; },
    async doRevert() {
      this.reverting = true;
      try {
        await this.$axios.post(`/api/stores/${this.campaign.id}/revert_edit_session`, { meta: { method: 'revert_edit_session', args: {} } });
        this.revertDialog = false;
        this.editSessionId = null;
        this.$emit('revert-completed');
      } catch (err) {
        console.error('[CampaignEditShell] revert failed', describeError(err));
      } finally {
        this.reverting = false;
      }
    },
  },
};
</script>

<style scoped>
.ces-root {
  display: grid;
  grid-template-columns: 300px 1fr;
  /* No height/min-height. The shell fills whatever space DistributorLayout's
   * .dist-main gives it; .dist-main is the scrolling element, and section
   * content drives the scroll height there. Forcing min-height: 100vh here
   * would push .dist-main taller than the viewport even when content is
   * small, double-stacking scrollbars. */
  background: var(--tf-surface-light, var(--tf-surface-light));
}

.ces-sidebar {
  background: var(--tf-bg);
  border-right: 1px solid var(--tf-border, var(--tf-border));
  padding: 14px 0 24px;
  /*
   * Pinned within the .dist-main scroll context (not the document).
   * top: 0 because .dist-main starts at the top of its own scroll
   * region -- the topbar/banner live OUTSIDE .dist-main and don't take
   * any scroll-anchor offset. max-height: 100% keeps the visible box
   * inside one .dist-main height; overflow-y: auto handles internal
   * scroll if the sidebar items exceed it on a short window.
   *
   * align-self: start is required so the grid cell sizes to the
   * sidebar's content rather than stretching to the section-body
   * height. Without it, the cell stretches as the body grows and the
   * sidebar sticks to top: 0 of a cell that's way taller than the
   * viewport -- it would scroll off the top with the rest of the body.
   */
  position: sticky;
  top: 0;
  align-self: start;
  /* --ces-main-h is the measured visible height of .dist-main (see
   * observeMainHeight); 100% is only the fallback when it is unavailable. */
  max-height: var(--ces-main-h, 100%);
  overflow-y: auto;
}

.ces-sidebar__head {
  padding: 6px 20px 14px;
  border-bottom: 1px solid var(--tf-border);
  margin-bottom: 8px;
}
.ces-sidebar__lbl {
  font: 500 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-quiet);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 4px;
}
.ces-sidebar__name {
  font: 500 16px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-1);
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ces-sidebar__meta {
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4);
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ces-sidebar__dates { color: var(--tf-fg-4); }

.ces-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font: 600 10px var(--tf-font-sans, 'Roboto', sans-serif);
  text-transform: uppercase;
  letter-spacing: .8px;
}
.ces-pill--draft   { background: var(--tf-warning-soft); color: var(--tf-warning-fg); }
.ces-pill--launched{ background: var(--tf-chip-success-bg); color: var(--tf-success-dark); }
.ces-pill--closed  { background: var(--tf-error-soft); color: var(--tf-red); }

.ces-grp {
  font: 600 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-quiet);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 14px 20px 6px;
}

.ces-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 20px;
  cursor: pointer;
  position: relative;
  border-left: 3px solid transparent;
  min-height: 36px;
  transition: background .15s;
  text-decoration: none;
  color: inherit;
}
.ces-step:hover { background: var(--tf-surface-light, var(--tf-surface-light)); }
.ces-step--act {
  background: var(--tf-surface-dark, var(--tf-surface-dark));
  border-left-color: var(--tf-primary, var(--tf-primary));
}
.ces-step__icon {
  width: 22px;
  display: flex;
  justify-content: center;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}
.ces-step__icon .tf-icon { font-size: 20px; }
.ces-step--act .ces-step__icon { color: var(--tf-primary, var(--tf-primary)); }
.ces-step__meta {
  flex: 1;
  min-width: 0;
}
.ces-step__t {
  font: 500 14px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-1);
  line-height: 1.3;
}
.ces-step--act .ces-step__t { color: var(--tf-primary, var(--tf-primary)); }
.ces-step__s {
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4);
  margin-top: 1px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ces-step--act .ces-step__s { color: var(--tf-primary, var(--tf-primary)); }
.ces-step__check {
  margin-left: 6px;
  font-size: 18px;
  flex-shrink: 0;
}
.ces-step--done .ces-step__check { color: var(--tf-success, var(--tf-success)); }
.ces-step__check--err { color: var(--tf-red, var(--tf-red)); }

/* Staff-only sections — dim in client-admin view */
body.tf-role-client .ces-step--staff-only {
  opacity: .36;
  pointer-events: none;
}

.ces-sidebar__foot {
  padding: 14px 20px;
  border-top: 1px solid var(--tf-border);
  margin-top: 8px;
}
.ces-ghost-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: none;
  color: var(--tf-fg-4);
  font: 500 13px var(--tf-font-sans, 'Roboto', sans-serif);
  cursor: pointer;
  border-radius: 6px;
  width: 100%;
  text-align: left;
}
.ces-ghost-btn:hover {
  background: var(--tf-surface-light, var(--tf-surface-light));
  color: var(--tf-primary, var(--tf-primary));
}
.ces-ghost-btn:disabled { opacity: .4; cursor: not-allowed; }
.ces-ghost-btn .tf-icon { font-size: 18px; }

.ces-content {
  padding: 32px 40px 80px;
  max-width: 1100px;
  position: relative;
}

/*
  The pager owns a ROW at the top of the content and sticks to the top of the
  scroll area. Content scrolls beneath an OPAQUE bar, so a control is either
  fully visible or fully hidden, never half under a floating pill where a
  click lands on Next (the Net Checkout switch right after Set PIN, PR 4808).
  The negative margins pull the row up over .ces-content's own top padding
  and out to its sides so the bar spans the content width; the pill inside is
  right-aligned as before.
*/
.ces-pager {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  background: var(--tf-bg);
  margin: -32px -40px 12px;
  padding: 12px 40px 8px;
  box-sizing: border-box;
  /* One height, shared: sticky elements inside the sections offset by it. */
  height: var(--ces-pager-h);
}
.ces-pager__pill {
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border, var(--tf-border));
  border-radius: 9999px;
  padding: 4px;
  box-shadow: 0 4px 14px rgba(19,55,82,.12);
}
.ces-pager__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: none;
  background: transparent;
  border-radius: 9999px;
  font: 500 13px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-2);
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}
.ces-pager__btn:hover { background: var(--tf-surface-light, var(--tf-surface-light)); color: var(--tf-primary, var(--tf-primary)); }
.ces-pager__btn--disabled { color: var(--tf-fg-5); cursor: not-allowed; pointer-events: none; }
.ces-pager__btn--next {
  background: var(--tf-primary, var(--tf-primary));
  color: var(--tf-bg);
  padding: 0 16px;
}
.ces-pager__btn--next:hover { background: var(--tf-primary-hover, var(--tf-primary-hover)); color: var(--tf-bg); }
.ces-pager__btn--next.ces-pager__btn--disabled { background: var(--tf-border); color: var(--tf-fg-5); }
.ces-pager__btn .tf-icon { font-size: 18px; }
.ces-pager__counter {
  font: 500 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-quiet);
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0 8px;
  border-left: 1px solid var(--tf-border, var(--tf-border));
  border-right: 1px solid var(--tf-border, var(--tf-border));
  height: 24px;
  display: inline-flex;
  align-items: center;
}

/*
 * .ces-page-head retained as a hook on the &lt;editor-section-header&gt; root so
 * the existing 28px spacing below the page head is preserved without
 * duplicating it inside EditorSectionHeader. The eyebrow / title /
 * description typography is owned by EditorSectionHeader's scoped styles.
 */
.ces-page-head {
  margin-bottom: 28px;
}

/* Storefront preview drawer */
.ces-preview-scrim {
  position: fixed;
  inset: 0;
  background: rgba(19,55,82,.42);
  backdrop-filter: blur(2px);
  z-index: 90;
  opacity: 0;
  pointer-events: none;
  transition: opacity .25s;
}
.ces-preview-scrim.open { opacity: 1; pointer-events: auto; }
.ces-preview-drawer {
  position: fixed;
  top: 0;
  right: 0;
  /* Stop above App.vue's fixed version/status bar (`<v-footer app>`), whose
     height Vuetify publishes as --v-layout-bottom on .v-main (inherited
     here; 0px when there is no bar). At 100vh the bar covered the caption's
     "open in a new tab" link, so a user could not click it. Same fix as
     StorefrontLayout's shell. */
  height: calc(100vh - var(--v-layout-bottom, 0px));
  width: min(1100px, 80vw);
  background: var(--tf-bg);
  z-index: 91;
  box-shadow: -12px 0 40px rgba(19,55,82,.18);
  transform: translateX(100%);
  transition: transform .28s cubic-bezier(.4,0,.2,1);
  display: flex;
  flex-direction: column;
}
.ces-preview-drawer.open { transform: translateX(0); }
.ces-preview-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--tf-border, var(--tf-border));
  background: var(--tf-surface-muted);
}
.ces-preview-ttl { font: 500 14px var(--tf-font-sans, 'Roboto', sans-serif); color: var(--tf-secondary, var(--tf-secondary)); }
.ces-preview-meta { font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif); color: var(--tf-fg-4); }
.ces-preview-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font: 600 10px var(--tf-font-sans, 'Roboto', sans-serif);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: middle;
  background: var(--tf-warning-soft);
  color: var(--tf-warning-fg);
}
.ces-preview-seg {
  display: flex;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border, var(--tf-border));
  border-radius: 9999px;
  padding: 3px;
  gap: 2px;
  margin-left: auto;
}
.ces-preview-seg button {
  border: none;
  background: transparent;
  padding: 6px 14px;
  border-radius: 9999px;
  font: 500 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.ces-preview-seg button.act {
  background: var(--tf-primary, var(--tf-primary));
  color: var(--tf-bg);
}
.ces-preview-seg button .tf-icon { font-size: 14px; }
.ces-preview-close {
  margin-left: 12px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-4);
}
.ces-preview-close:hover { background: var(--tf-surface-dark); color: var(--tf-secondary, var(--tf-secondary)); }
.ces-preview-body {
  flex: 1;
  overflow: auto;
  /* Cool-gray preview canvas backdrop. No exact --tf-* match
     (between surface-dark and material grey-300). Route via fallback
     so a token can be added later without touching this file. */
  background: var(--tf-surface-canvas, #f0f0f3);
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 24px;
}
/*
 * Preview stage. `--desktop` fills the drawer width; `--mobile`
 * constrains the frame to a phone-width column, centered. The stage is
 * a flex column so the caption sits beneath the framed storefront.
 */
.ces-preview-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;
}
.ces-preview-frame {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
}
.ces-preview-stage--mobile .ces-preview-frame {
  /* Center a ~phone-width column; the iframe.mobile below sets the
     exact 390px device width. */
  max-width: 390px;
  margin: 0 auto;
}
.ces-preview-body iframe {
  border: none;
  background: var(--tf-bg);
  box-shadow: 0 8px 32px rgba(0,0,0,.12);
  border-radius: 8px;
  display: block;
}
.ces-preview-body iframe.desktop {
  width: 100%;
  height: 100%;
}
.ces-preview-body iframe.mobile {
  width: 390px;
  max-width: 100%;
  height: 100%;
}
.ces-preview-caption {
  flex-shrink: 0;
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4);
  text-align: center;
  margin: 0;
}
.ces-preview-empty {
  align-self: center;
  text-align: center;
  color: var(--tf-fg-4);
}
.ces-preview-empty .tf-icon { font-size: 48px; opacity: .5; display: block; margin-bottom: 12px; }
.ces-preview-live {
  flex-shrink: 0;
  padding: 10px 20px;
  border-bottom: 1px solid var(--tf-border, rgba(0,0,0,.12));
  background: var(--tf-chip-success-bg);
}
.ces-preview-live__link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 500 14px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-success-dark);
  text-decoration: none;
}
.ces-preview-live__link:hover { text-decoration: underline; }
.ces-preview-live__link .tf-icon { font-size: 18px; }
.ces-preview-link {
  color: var(--tf-primary, var(--tf-primary));
  font-weight: 500;
}
.ces-preview-link:hover { text-decoration: underline; }
.ces-preview-link code {
  font-family: var(--tf-font-mono, monospace);
  font-size: 12px;
  background: rgba(0,0,0,.05);
  padding: 2px 4px;
  border-radius: 3px;
}

@media (max-width: 1100px) {
  .ces-root { grid-template-columns: 64px 1fr; }
  .ces-sidebar__head .ces-sidebar__name,
  .ces-sidebar__head .ces-sidebar__meta,
  .ces-sidebar__head .ces-sidebar__lbl { display: none; }
  .ces-grp { display: none; }
  .ces-step { padding: 10px 0; justify-content: center; border-left-width: 0; border-right: 3px solid transparent; }
  .ces-step--act { border-right-color: var(--tf-primary, var(--tf-primary)); }
  .ces-step__meta, .ces-step__check { display: none; }
  .ces-step__icon { width: auto; }
  .ces-ghost-btn { justify-content: center; padding: 10px 0; }
  .ces-ghost-btn span:not(.tf-icon) { display: none; }
  .ces-content { padding: 24px 24px 80px; }
  .ces-pager { margin: -24px -24px 12px; padding: 12px 24px 8px; }
}
</style>

<!--
  Flush-layout escape from DistributorLayout's `.dist-main` gutter while the
  store editor shell is mounted.

  Why unscoped: this selector intentionally reaches OUT of the component to
  the parent layout shell. A scoped `body.tf-store-editor-active` selector
  would still scope the *descendant* selector and miss `.dist-main`, which
  lives in DistributorLayout, not here.

  Why this is safe:
    - The body class is added/removed by this component's lifecycle
      (refcounted, see the script block), so the rule only applies while a
      store editor shell is actually on screen.
    - It targets a single, well-known DistributorLayout class.
    - Only the store editor route mounts this shell, so the class never
      bleeds onto Stores, Orders, Reports or any other distributor page --
      each of which still wants the 24px gutter.

  Pattern reference: ProductEditor.vue does the same with
  `body.tf-product-editor-active`, and StoreEditShell already uses
  `body.tf-role-client` for a comparable layout-shell escape.
-->
<style>
/*
  Drop ONLY `.dist-main`'s padding, so the editor's left-nav sits flush
  against the page header above it and the distributor left-nav beside it
  (Jake, 2026-08-26: "remove that to save space"). We deliberately do NOT
  touch `.dist-main`'s `overflow-y: auto` -- the shell's sticky sidebar is
  positioned within that scroll context and pinning it depends on the base
  rule staying put.

  The nav's INTERNAL padding is untouched: `.ces-sidebar`'s own
  `padding: 14px 0 24px`, `.ces-sidebar__head`'s `6px 20px 14px` and each
  `.ces-step`'s `6px 20px` all survive, so the rows keep their breathing
  room. Only the OUTER offset goes.

  The content pane keeps its own spacing from `.ces-content`
  (`padding: 32px 40px 80px`), which is why the section body does not go
  flush with it -- that padding is measured and asserted by
  frontend/tools/store-editor-geometry/measure.mjs.
*/
body.tf-store-editor-active {
    /* Height of the pager's sticky row (.ces-pager): row padding 12 + 8 and
       the 46px pill. Sticky elements in the sections (Checkout preview
       toolbar, email-template preview, decorations rail) use it as their
       `top`, and scroll-padding-top below reads it too. */
    --ces-pager-h: 66px;
}
body.tf-store-editor-active .dist-main {
    padding: 0;
    /*
      Secondary measure to the pager's own sticky row (.ces-pager): the row
      is --ces-pager-h tall, so scrollIntoView, focus and scripted scrolls aim
      below it (plus 22px) instead of parking a control under the bar. Wheel and
      scrollbar scrolling cannot hide a control half under a pill any more
      because the bar is opaque and spans the content width.
    */
    scroll-padding-top: calc(var(--ces-pager-h) + 22px);
}
</style>
